import React, { Fragment, FunctionComponent } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { getCountryName } from 'helpers/countries';
import { getCurrencyName } from 'helpers/currencies';
import { formatHebrewFromISO } from 'components/inputs/HebrewDateInput';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';

import { IDeathShowProps } from './types';
import './i18n';

const DeathsShow: FunctionComponent<IDeathShowProps> = (props: IDeathShowProps) => {
  const { item, showTitle = true } = props;

  return (
    <ViewContainer auth data-testid="deaths-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <div />
            <Title>{i18next.t<string>('DEATHS_SHOW:TITLE')}</Title>
            <CustomIconButton
              onClick={() => history.push(`/public/procedures/forms/deaths/${item?.id}/edit`)}
            >
              <EditOutlined />
            </CustomIconButton>
          </TitleContainer>
        )}
        {item && (
          <>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:PERSON_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:LAST_NAME')}</td>
                  <td>{item.person.lastName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:NAME')}</td>
                  <td>{item.person.name}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_TYPE')}</td>
                  <td>{item.person.idCardTypeName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD')}</td>
                  <td>{item.person.idCard}</td>
                </tr>
                {item.person.idCardFile && (
                  <tr>
                    <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardFile} title="idCardFile" />
                    </td>
                  </tr>
                )}
                {item.person.idCardBackFile && (
                  <tr>
                    <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_BACK_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.idCardBackFile} title="idCardBackFile" />
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:AGE')}</td>
                  <td>{item.person.age}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:COUNTRY')}</td>
                  <td>{getCountryName(item.person.country)}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:ADDRESS')}</td>
                  <td>{item.person.address}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:BIRTHDATE')}</td>
                  <td>{item.person.birthdate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:DEATH_DATE')}</td>
                  <td>{item.person.deathDate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:DEATH_PLACE')}</td>
                  <td>{item.person.deathPlace}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:DEATH_DATE_HEBREW')}</td>
                  <td>
                    {formatHebrewFromISO(item.person.deathDateHebrew) ||
                      item.person.deathDateHebrew}
                  </td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:COMMUNITY')}</td>
                  <td>{item.person.community}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CIVIL_STATE')}</td>
                  <td>{item.person.civilStateName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:TRIBE')}</td>
                  <td>{item.person.tribeName}</td>
                </tr>
                {item.person.ketubaFile && (
                  <tr>
                    <td>{i18next.t<string>('DEATHS_SHOW:KETUBA_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.ketubaFile} title="ketubaFile" />
                    </td>
                  </tr>
                )}
                {item.person.marriageCertificateFile && (
                  <tr>
                    <td>{i18next.t<string>('DEATHS_SHOW:MARRIAGE_CERTIFICATE_FILE')}</td>
                    <td>
                      <FilePreview
                        src={item.person.marriageCertificateFile}
                        title="marriageCertificateFile"
                      />
                    </td>
                  </tr>
                )}
                {item.person.getFile && (
                  <tr>
                    <td>{i18next.t<string>('DEATHS_SHOW:GET_FILE')}</td>
                    <td>
                      <FilePreview src={item.person.getFile} title="getFile" />
                    </td>
                  </tr>
                )}
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:SPOUSE_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:LAST_NAME')}</td>
                  <td>{item.spouse.lastName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:NAME')}</td>
                  <td>{item.spouse.name}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:WEDDING_TEMPLE')}</td>
                  <td>{item.spouse.weddingTemple}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:ALIVE')}</td>
                  <td>
                    {item.spouse.alive
                      ? i18next.t<string>('DEATHS_SHOW:YES')
                      : i18next.t<string>('DEATHS_SHOW:NO')}
                  </td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CEMENTERY')}</td>
                  <td>
                    {item.spouse.alive ? '-' : item.spouse.cementeryName || item.spouse.cementery}
                  </td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:OTHER_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:FATHER_INFO')}</td>
                  <td>{item.family.fatherInfo}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:MOTHER_INFO')}</td>
                  <td>{item.family.motherInfo}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CEMENTERY')}</td>
                  <td>{item.family.cementeriesName || item.family.cementeries}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:CHILDREN_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CHILDREN_INFO')}</td>
                  <td>{item.family.childrenInfo}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:LOCATION_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CEMENTERY')}</td>
                  <td>{item.location.cementeryName || item.location.cementery}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:PLOT')}</td>
                  <td>{item.location.plot}</td>
                </tr>
              </tbody>
            </TableContainer>
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:SIGNERS_INFORMATION')}
            </Typography>
            <br />
            {item.signers.map(
              (signer, i) =>
                signer.name &&
                signer.idCard && (
                  <TableContainer key={`${signer.idCard}-${i.toString()}-information`}>
                    <tbody>
                      <tr>
                        <td>{`${i18next.t<string>('DEATHS_SHOW:SIGNER_NAME')} ${i + 1}`}</td>
                        <td>{signer.name}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_TYPE')}</td>
                        <td>{signer.idCardTypeName}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD')}</td>
                        <td>{signer.idCard}</td>
                      </tr>
                      {signer.idCardFile && (
                        <tr>
                          <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_FILE')}</td>
                          <td>
                            <FilePreview src={signer.idCardFile} title="idCardFile" />
                          </td>
                        </tr>
                      )}
                      {signer.idCardBackFile && (
                        <tr>
                          <td>{i18next.t<string>('DEATHS_SHOW:ID_CARD_BACK_FILE')}</td>
                          <td>
                            <FilePreview src={signer.idCardBackFile} title="idCardBackFile" />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:EMAIL')}</td>
                        <td>{signer.email}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:PHONE')}</td>
                        <td>{`${signer.areaCode ? `(${signer.areaCode}) ` : ''}${
                          signer.phone
                        }`}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:TELEPHONE')}</td>
                        <td>{signer.telephone}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:ADDRESS')}</td>
                        <td>{signer.address}</td>
                      </tr>
                      <tr>
                        <td>{i18next.t<string>('DEATHS_SHOW:KINSHIP')}</td>
                        <td>{signer.kinship}</td>
                      </tr>
                    </tbody>
                  </TableContainer>
                ),
            )}
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_SHOW:PAYMENT_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:CURRENCY')}</td>
                  <td>{getCurrencyName(item.payment.currency)}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:AMOUNT')}</td>
                  <td>{item.payment.amount}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:PAYMENT_CONDITIONS')}</td>
                  <td>{item.payment.paymentConditions}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:PAYMENTS_DONE')}</td>
                  <td>{item.payment.paymentsDone}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('DEATHS_SHOW:GARAGE')}</td>
                  <td>{item.payment.garage}</td>
                </tr>
              </tbody>
            </TableContainer>
            {item.comments && (
              <>
                <Typography variant="h6">{i18next.t<string>('DEATHS_SHOW:COMMENTS')}</Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default DeathsShow;
